import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from '../components/shared/Navigation';
import Footer from '../components/shared/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ContactPage() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest_type: location.state?.unitId ? 'for_sale' : 'rental',
    unit_id: location.state?.unitId || '',
    message: '',
    move_in_date: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit inquiry');
      }

      toast.success('Thank you! We\'ll be in touch soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        interest_type: 'rental',
        unit_id: '',
        message: '',
        move_in_date: ''
      });
    } catch (error) {
      console.error(error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navigation />
      
      <div className="container section-spacing max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-[var(--font-display)] tracking-[-0.02em] mb-4">
            Contact Us
          </h1>
          <p className="text-lg text-muted-foreground">
            Have questions? We're here to help.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle>Send us a message</CardTitle>
              <CardDescription>Fill out the form and we'll get back to you within 24 hours</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="interest_type">I'm interested in</Label>
                  <Select
                    value={formData.interest_type}
                    onValueChange={(value) => setFormData({...formData, interest_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rental">Renting</SelectItem>
                      <SelectItem value="for_sale">Buying</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="move_in_date">Desired Move-in Date</Label>
                  <Input
                    id="move_in_date"
                    type="date"
                    value={formData.move_in_date}
                    onChange={(e) => setFormData({...formData, move_in_date: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  />
                </div>

                <Button
                  data-testid="sales-inquiry-submit-button"
                  type="submit"
                  className="w-full bg-[#A06A3B] text-[#FBF8F2] hover:bg-[#6F4526] rounded-[var(--btn-radius)]"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Visit Us</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Schedule a tour to see Ellis Row in person. Submit the form and we'll contact you within 24 hours.
                </p>
                <div className="space-y-2 text-sm">
                  <p><strong>Response Time:</strong> Within 24 hours</p>
                  <p><strong>Tour Hours:</strong> Mon-Sat, 10am-6pm</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Property Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Rental Units:</span>
                  <span className="font-medium">4 available</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">For Sale Units:</span>
                  <span className="font-medium">3 available</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Size:</span>
                  <span className="font-medium">4 bed, 2.5 bath, 1,680 sqft</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Move-in Ready:</span>
                  <span className="font-medium">2026</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}